class ShippingDatePicker extends HTMLElement {
  static get observedAttributes() { return ['data-digital']; }

  constructor() {
    super();
    this.expressFeeDates = new Set();
    this.flatpickrInstance = null;
    this.expressFeeValue = 15;
    this.expressFeeVariantId = 45962140254460;
    this.isDigital = false;
  }

  // tiny helper to coerce attribute to boolean
  _coerceBool(v) {
    return !(v === null || v === undefined || v === '0' || String(v).toLowerCase() === 'false');
  }

  attributeChangedCallback(name, oldV, newV) {
    if (name === 'data-digital') {
      this.isDigital = this._coerceBool(newV);
      // If already initialized, reapply styles/fees+info for current month/selection
      if (this.flatpickrInstance) {
        this.expressFeeDates = this.isDigital ? new Set() : this.getExpressFeeDates();
        this.reapplyExpressFeeStyles(this.flatpickrInstance);
        const selected = this.flatpickrInstance.selectedDates?.[0];
        if (selected) this.handleDateSelection(selected);
      }
    }
  }

  setExpressFees(expressFeeValue, expressFeeVariantId) {
    this.expressFeeValue = expressFeeValue;
    this.expressFeeVariantId = expressFeeVariantId;
  }
  
  connectedCallback() {
    this.isDigital = this._coerceBool(this.getAttribute('data-digital'));

    // Don't use Shadow DOM for Flatpickr - it needs direct DOM access
    this.innerHTML = `
      <div class="shipping-date-picker-container">
        <input type="text" id="scheduleDate" placeholder="Select delivery date">
        <div id="shippingFeeInfo"></div>
      </div>
    `;
    
    this.dateInput = this.querySelector('#scheduleDate');
    this.shippingInfo = this.querySelector('#shippingFeeInfo');
    
    // Add styles to the document if not already present
    if (!document.getElementById('shipping-date-picker-styles')) {
      const style = document.createElement('style');
      style.id = 'shipping-date-picker-styles';
      style.textContent = `
        .shipping-date-picker-container { position: relative; width: 100%; }
        .shipping-date-picker-container input[type="text"] {
          width: 100%; padding: 10px 15px; border: 1px solid #ddd; border-radius: 4px;
          font-size: 14px; transition: border-color 0.3s; color: #333; font-family: inherit; background-color: white;
        }
        .shipping-date-picker-container input[type="text"]:focus { outline: none; border-color: #333; }
        .shipping-date-picker-container #shippingFeeInfo { margin-top: 5px; font-size: 12px; color: #666; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        .shipping-date-picker-container .express-fee { color: #ff6b6b; font-weight: 600; margin-top: 5px; }
        .shipping-date-picker-container .standard-shipping { color: #4CAF50; margin-top: 5px; }
        /* Flatpickr */
        .flatpickr-day.flatpickr-disabled { color: #ddd !important; background: #f5f5f5 !important; cursor: not-allowed !important; }
        .flatpickr-day.express-fee:not(.flatpickr-disabled) { background: #fff5f5 !important; color: #ff6b6b !important; font-weight: bold; }
        .flatpickr-day.express-fee.selected { background: #ff6b6b !important; color: white !important; }
      `;
      document.head.appendChild(style);
    }
    
    // Load Flatpickr library and CSS
    this.loadFlatpickr().then(() => {
      this.initializeDatePicker();
    });
  }
  
  async loadFlatpickr() {
    if (window.flatpickr) return Promise.resolve();
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css';
    document.head.appendChild(link);
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/flatpickr';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Flatpickr'));
      document.head.appendChild(script);
    });
  }
  
  initializeDatePicker() {
    const brisbaneTime = this.getBrisbaneTime();
    
    // Calculate express fee dates (empty set if Digital)
    this.expressFeeDates = this.isDigital ? new Set() : this.getExpressFeeDates();
    
    // Get first available date
    const firstAvailable = this.getFirstAvailableDate();
    
    // Initialize Flatpickr
    this.flatpickrInstance = flatpickr(this.dateInput, {
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "F j, Y",
      minDate: "today",
      defaultDate: firstAvailable,
      disable: [
        (date) => !this.isDateAvailable(date, brisbaneTime)
      ],
      onDayCreate: (dObj, dStr, fp, dayElem) => {
        if (this.isDigital) return;
        const date = dayElem.dateObj;
        if (date) {
          const dateStr = this.formatDateString(date);
          if (this.expressFeeDates.has(dateStr) && !dayElem.classList.contains('flatpickr-disabled')) {
            dayElem.classList.add('express-fee');
          }
        }
      },
      onChange: (selectedDates, dateStr, instance) => {
        if (selectedDates.length > 0) {
          this.handleDateSelection(selectedDates[0]);
          setTimeout(() => this.reapplyExpressFeeStyles(instance), 10);
        }
      },
      onOpen: (selectedDates, dateStr, instance) => {
        setTimeout(() => this.reapplyExpressFeeStyles(instance), 10);
      },
      onMonthChange: (selectedDates, dateStr, instance) => {
        setTimeout(() => this.reapplyExpressFeeStyles(instance), 10);
      }
    });
    
    // Trigger initial date selection
    if (firstAvailable) {
      this.handleDateSelection(firstAvailable);
    }
  }
  
  reapplyExpressFeeStyles(instance) {
    if (!instance) return;
    const dayElements = instance.calendarContainer.querySelectorAll('.flatpickr-day:not(.flatpickr-disabled)');
    // Always remove any lingering express classes first
    instance.calendarContainer.querySelectorAll('.flatpickr-day.express-fee')
      .forEach(el => el.classList.remove('express-fee'));
    if (this.isDigital) return;

    dayElements.forEach(dayElem => {
      const date = dayElem.dateObj;
      if (date) {
        const dateStr = this.formatDateString(date);
        if (this.expressFeeDates.has(dateStr)) {
          dayElem.classList.add('express-fee');
        }
      }
    });
  }
  
  formatDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  getExpressFeeDates() {
    if (this.isDigital) return new Set();
    const dates = new Set();
    const availableDates = [];
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const brisbaneTime = this.getBrisbaneTime();
    while (availableDates.length < 3) {
      currentDate = new Date(currentDate);
      currentDate.setDate(currentDate.getDate() + 1);
      if (this.isDateAvailable(currentDate, brisbaneTime)) {
        availableDates.push(this.formatDateString(currentDate));
      }
    }
    availableDates.forEach(date => dates.add(date));
    return dates;
  }
  
  getBrisbaneTime() {
    const now = new Date();
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
    const brisbaneTime = new Date(utcTime + (10 * 3600000));
    return brisbaneTime;
  }
  
  isBusinessDay(date) {
    const day = date.getDay();
    return day !== 0 && day !== 6;
  }
  
  isDateAvailable(date, currentBrisbaneTime = null) {
    // Business-day check always applies
    if (!this.isBusinessDay(date)) return false;

    // Brisbane "now"
    if (!currentBrisbaneTime) currentBrisbaneTime = this.getBrisbaneTime();

    // Diff in days vs today (00:00 local)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const diffDays = Math.round((checkDate.getTime() - today.getTime()) / 86400000);

    // --- DIGITAL RULES ---
    if (this.isDigital) {
        // Allow same-day if it's a business day and it's before 5pm Brisbane
        if (diffDays === 0) {
        return currentBrisbaneTime.getHours() < 17;
        }
        // Future business days are OK
        return true;
    }

    // --- PHYSICAL (existing rules) ---
    // Can't select past dates or today
    if (diffDays <= 0) return false;

    // +1 day is always blocked
    if (diffDays === 1) return false;

    // +2 day is blocked after 11am Brisbane, or if today (Brisbane) isn't a business day
    if (diffDays === 2) {
        const todayBrisbane = new Date(currentBrisbaneTime);
        if (!this.isBusinessDay(todayBrisbane)) return false;
        if (currentBrisbaneTime.getHours() >= 11) return false;
    }

    return true;
  }

  
  getFirstAvailableDate() {
    let date = new Date();
    date.setHours(0, 0, 0, 0);
    const brisbaneTime = this.getBrisbaneTime();
    while (!this.isDateAvailable(date, brisbaneTime)) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }
  
  handleDateSelection(date) {
    const selectedDate = this.formatDateString(date);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const formattedDate = date.toLocaleDateString('en-AU', options);

    if (this.isDigital) {
      // Digital — never show express fee disclaimer
      this.shippingInfo.innerHTML = `<div>${formattedDate}</div>`;
      this.dispatchEvent(new CustomEvent('datechange', {
        detail: {
          date: selectedDate,
          hasExpressFee: false,
          fee: 0,
          feeProductVariantId: null
        },
        bubbles: true,
        composed: true
      }));
      return;
    }
    
    if (this.expressFeeDates.has(selectedDate)) {
      this.shippingInfo.innerHTML = `
        <div>${formattedDate}</div>
        <div class="express-fee"><strong>+$${this.expressFeeValue} express shipping fee applies</strong></div>
      `;
    } else {
      this.shippingInfo.innerHTML = `
        <div>${formattedDate}</div>
        <div class="standard-shipping">Standard shipping</div>
      `;
    }
    
    this.dispatchEvent(new CustomEvent('datechange', {
      detail: {
        date: selectedDate,
        hasExpressFee: this.expressFeeDates.has(selectedDate),
        fee: this.expressFeeDates.has(selectedDate) ? this.expressFeeValue : 0,
        feeProductVariantId: this.expressFeeDates.has(selectedDate) ? this.expressFeeVariantId : null,
      },
      bubbles: true,
      composed: true
    }));
  }
  
  // Public API
  get value() { return this.dateInput.value; }
  set value(date) { if (this.flatpickrInstance) this.flatpickrInstance.setDate(date); }
  get shippingFee() { return 0; } // Always 0 (component isn’t authoritative; event detail is used upstream)
  
  reset() {
    if (this.flatpickrInstance) this.flatpickrInstance.clear();
    this.shippingInfo.innerHTML = '';
  }
  
  disconnectedCallback() {
    if (this.flatpickrInstance) this.flatpickrInstance.destroy();
  }
}

customElements.define('shipping-date-picker', ShippingDatePicker);
